/**
 * I18n Store
 *
 * Store Zustand para gerenciamento de estado de internacionalizacao.
 * Implementa cache local e sincronizacao com API i18n.
 *
 * @since 2025-12-16
 * @epic INIT-0045-03
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  TranslationResponse,
  I18nStore,
  CacheEntry,
} from '@/types/i18n';
import { i18nApi } from '@/services/i18nApi';

// Chave para storage local
const STORAGE_KEY = 'tmf-i18n-store';

// TTL do cache local (5 minutos)
const CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * Gera chave de cache para traducao
 */
const getTranslationCacheKey = (model: string, modelId: number, languageCode: string): string => {
  return `${model}:${modelId}:${languageCode}`;
};

/**
 * Gera chave de cache para mensagens
 */
const getMessageCacheKey = (category: string, languageCode: string): string => {
  return `msg:${category}:${languageCode}`;
};

/**
 * Verifica se entrada de cache ainda e valida
 */
const isCacheValid = <T>(entry: CacheEntry<T> | undefined): entry is CacheEntry<T> => {
  if (!entry) return false;
  return Date.now() - entry.timestamp < CACHE_TTL_MS;
};

/**
 * I18n Store com Zustand
 */
export const useI18nStore = create<I18nStore>()(
  persist(
    (set, get) => ({
      // ===== State =====
      currentLanguage: 'pt_BR',
      languages: [],
      translationCache: new Map(),
      messageCache: new Map(),
      isLoading: false,
      error: null,
      initialized: false,

      // ===== Actions =====

      /**
       * Inicializa o store carregando idiomas e configuracoes
       */
      initialize: async () => {
        const state = get();
        if (state.initialized) return;

        set({ isLoading: true, error: null });

        try {
          // Carregar idiomas ativos
          const languages = await i18nApi.getActiveLanguages();

          // Determinar idioma atual
          let currentLanguage = state.currentLanguage;

          // Verificar preferencia do navegador
          const browserLang = navigator.language.replace('-', '_');
          const matchedLang = languages.find(l => l.language_code === browserLang);
          if (matchedLang) {
            currentLanguage = matchedLang.language_code;
          }

          // Verificar se ha idioma salvo
          const savedLang = localStorage.getItem('tmf_language');
          if (savedLang) {
            const savedMatchedLang = languages.find(l => l.language_code === savedLang);
            if (savedMatchedLang) {
              currentLanguage = savedMatchedLang.language_code;
            }
          }

          set({
            languages,
            currentLanguage,
            initialized: true,
            isLoading: false,
          });
        } catch (error) {
          console.error('[i18n] Erro ao inicializar:', error);
          set({
            error: error as Error,
            isLoading: false,
            initialized: true, // Marcar como inicializado mesmo com erro
          });
        }
      },

      /**
       * Muda o idioma atual
       */
      setLanguage: (code: string) => {
        const { languages } = get();
        const language = languages.find(l => l.language_code === code);

        if (language) {
          localStorage.setItem('tmf_language', code);
          set({ currentLanguage: code });

          // Limpar cache ao mudar idioma (sera recarregado sob demanda)
          get().clearCache();
        } else {
          if (import.meta.env.DEV) console.warn(`[i18n] Idioma nao encontrado: ${code}`);
        }
      },

      /**
       * Carrega lista de idiomas
       */
      loadLanguages: async () => {
        set({ isLoading: true });

        try {
          const languages = await i18nApi.getActiveLanguages();
          set({ languages, isLoading: false });
        } catch (error) {
          set({ error: error as Error, isLoading: false });
          throw error;
        }
      },

      /**
       * Obtem traducao (do cache ou API)
       */
      getTranslation: async (model: string, modelId: number, field?: string) => {
        const { currentLanguage, translationCache } = get();
        const cacheKey = getTranslationCacheKey(model, modelId, currentLanguage);

        // Verificar cache local
        const cached = translationCache.get(cacheKey);
        if (isCacheValid(cached)) {
          return cached.data;
        }

        // Buscar da API
        try {
          const translation = await i18nApi.getTranslation(model, modelId, currentLanguage, field);

          // Salvar no cache
          const newCache = new Map(translationCache);
          newCache.set(cacheKey, {
            data: translation,
            timestamp: Date.now(),
          });
          set({ translationCache: newCache });

          return translation;
        } catch (error) {
          console.error(`[i18n] Erro ao obter traducao ${model}:${modelId}:`, error);
          throw error;
        }
      },

      /**
       * Obtem mensagens de categoria (do cache ou API)
       */
      getMessages: async (category: string) => {
        const { currentLanguage, messageCache } = get();
        const cacheKey = getMessageCacheKey(category, currentLanguage);

        // Verificar cache local
        const cached = messageCache.get(cacheKey);
        if (isCacheValid(cached)) {
          return cached.data;
        }

        // Buscar da API
        try {
          const response = await i18nApi.getMessages(category, currentLanguage);

          // Salvar no cache
          const newCache = new Map(messageCache);
          newCache.set(cacheKey, {
            data: response.messages,
            timestamp: Date.now(),
          });
          set({ messageCache: newCache });

          return response.messages;
        } catch (error) {
          console.error(`[i18n] Erro ao obter mensagens ${category}:`, error);
          throw error;
        }
      },

      /**
       * Invalida cache de traducao
       */
      invalidateTranslation: (model: string, modelId?: number) => {
        const { translationCache, currentLanguage } = get();
        const newCache = new Map(translationCache);

        // Padrão de chave para filtrar
        const prefix = modelId
          ? getTranslationCacheKey(model, modelId, currentLanguage)
          : `${model}:`;

        // Remover entradas que correspondem
        Array.from(newCache.keys()).forEach(key => {
          if (key.startsWith(prefix)) {
            newCache.delete(key);
          }
        });

        set({ translationCache: newCache });
      },

      /**
       * Invalida cache de mensagens
       */
      invalidateMessages: (category?: string) => {
        const { messageCache, currentLanguage } = get();
        const newCache = new Map(messageCache);

        if (category) {
          const cacheKey = getMessageCacheKey(category, currentLanguage);
          newCache.delete(cacheKey);
        } else {
          // Limpar todas as mensagens do idioma atual
          Array.from(newCache.keys()).forEach(key => {
            if (key.endsWith(`:${currentLanguage}`)) {
              newCache.delete(key);
            }
          });
        }

        set({ messageCache: newCache });
      },

      /**
       * Limpa todo o cache
       */
      clearCache: () => {
        set({
          translationCache: new Map(),
          messageCache: new Map(),
        });
      },

      /**
       * Set error
       */
      setError: (error: Error | null) => {
        set({ error });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Persistir apenas o idioma atual
        currentLanguage: state.currentLanguage,
      }),
    }
  )
);

// ===== Convenience Functions =====

/**
 * Obtem o idioma atual (fora de componentes React)
 */
export const getCurrentLanguage = (): string => {
  return useI18nStore.getState().currentLanguage;
};

/**
 * Muda o idioma (fora de componentes React)
 */
export const setLanguage = (code: string): void => {
  useI18nStore.getState().setLanguage(code);
};

/**
 * Obtem traducao de modelo (fora de componentes React)
 */
export const getTranslation = async (
  model: string,
  modelId: number,
  field?: string
): Promise<TranslationResponse> => {
  return useI18nStore.getState().getTranslation(model, modelId, field);
};

/**
 * Obtem mensagens de categoria (fora de componentes React)
 */
export const getMessages = async (category: string): Promise<Record<string, string>> => {
  return useI18nStore.getState().getMessages(category);
};

/**
 * Inicializa i18n (chamar no App.tsx)
 */
export const initializeI18n = async (): Promise<void> => {
  return useI18nStore.getState().initialize();
};

export default useI18nStore;
